import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CareerOutlook from './CareerOutlook';

const {
  FiCalculator,
  FiFileText,
  FiHelpCircle,
  FiSearch,
  FiWrench,
} = FiIcons;

const tools = [
  {
    icon: FiCalculator,
    title: 'E/M Decision Tree Calculator',
    text: 'Calculate 2023/2024 AMA Medical Decision Making (MDM) levels and CPT office visit codes (99202–99215) using 2-of-3 scoring or total time.',
    action: 'Open E/M Calculator →',
    path: '/em-calculator',
  },
  {
    icon: FiWrench,
    title: 'CPT / HCPCS Modifier Selector Tool',
    text: 'Step-by-step decision wizard for CPT modifiers (-25, -59, -50). Eliminate unbundling and duplicate service billing errors on CPC exams.',
    action: 'Launch Modifier Wizard →',
    path: '/modifier-tool',
  },
  {
    icon: FiSearch,
    title: 'Medical Terminology Dictionary',
    text: 'Searchable medical terminology dictionary. Deconstruct complex prefixes, root words, and suffixes to decode unknown procedural terms.',
    action: 'Search Dictionary →',
    path: '/term-dictionary',
  },
  {
    icon: FiFileText,
    title: 'ICD-10-CM Guidelines Practice Quiz',
    text: 'Interactive ICD-10-CM coding guidelines practice quiz testing sequencing rules, excludes notes, and diagnosis selection with immediate rationales.',
    action: 'Start Guidelines Quiz →',
    path: '/icd10-quiz',
  },
];

const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Can I learn medical billing and coding online?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Our study guide provides 100% instant online access to core curriculum modules covering ICD-10-CM, CPT®, HCPCS Level II, and medical decision-making (MDM) without requiring paid course enrollment or mandatory fees.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need previous healthcare experience?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. The curriculum starts with terminology and industry foundations before introducing coding concepts.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is my progress saved?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Completed lessons are stored locally in your current browser without requiring an account.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between AAPC (CPC) and AHIMA (CCA/CCS-P) certifications?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'AAPC offers the Certified Professional Coder (CPC®) credential, which focuses heavily on physician office and outpatient procedural coding. AHIMA offers the Certified Coding Associate (CCA®) and Certified Coding Specialist - Physician-based (CCS-P®) credentials, covering both inpatient and outpatient settings.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do interactive tools like the E/M Calculator and Modifier Selector help on coding exams?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Interactive calculators break down complex guidelines—such as 2023/2024 AMA Medical Decision Making scoring rules and CPT modifier selection (-25, -59, -50)—into clear decision steps. Testing yourself with live scenarios builds the clinical intuition needed to solve exam case studies quickly.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are these practice modules aligned with current ICD-10-CM and CPT guidelines?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, all lessons, quizzes, and decision trees are aligned with official CMS, AMA, and ICD-10-CM coding guidelines and conventions used across AAPC and AHIMA exam blueprints.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this a certification program?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. This is an independent educational study resource and does not award credentials or certification.',
      },
    },
  ],
};

function StudyTools() {
  const navigate = useNavigate();

  const openTool = (path) => {
    navigate(path);
  };

  const handleCardKeyDown = (event, path) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openTool(path);
    }
  };

  return (
    <>
      <section className="tools-section interactive-tools-section" id="practice-tools">
        <style>
          {`
            .interactive-tools-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .interactive-tool-card {
              display: flex;
              flex-direction: column;
              min-height: 305px;
              cursor: pointer;
              background: white;
              transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s;
            }

            .interactive-tool-card:hover,
            .interactive-tool-card:focus-visible {
              border-color: #9bdad4;
              transform: translateY(-5px);
              box-shadow: 0 20px 45px rgba(15, 42, 66, 0.11);
            }

            .interactive-tool-card > svg {
              width: 27px;
              height: 27px;
              margin-bottom: 23px;
              color: #0d9488;
            }

            .interactive-tool-card p {
              margin-bottom: 25px;
            }

            .interactive-tool-action {
              margin-top: auto;
              align-self: flex-start;
              padding: 0;
              color: #0f766e;
              font-size: 0.8rem;
              font-weight: 800;
              background: transparent;
              border: 0;
              transition: color 0.2s, transform 0.2s;
            }

            .interactive-tool-action:hover {
              color: #082f49;
              transform: translateX(3px);
            }

            @media (max-width: 680px) {
              .interactive-tools-grid {
                grid-template-columns: 1fr;
              }

              .interactive-tool-card {
                min-height: 0;
              }
            }
          `}
        </style>

        <div className="container">
          <div className="centered-heading">
            <span className="section-kicker">Hands-On Practice</span>
            <h2>Interactive Medical Coding Tools</h2>
            <p>
              Master real-world coding logic instantly using step-by-step decision trees, CPT modifier lookups, and interactive diagnostic quizzes.
            </p>
          </div>

          <div className="tools-grid interactive-tools-grid">
            {tools.map((tool) => (
              <article
                className="tool-card interactive-tool-card"
                key={tool.title}
                role="link"
                tabIndex="0"
                onClick={() => openTool(tool.path)}
                onKeyDown={(event) => handleCardKeyDown(event, tool.path)}
                aria-label={`Open ${tool.title}`}
              >
                <SafeIcon icon={tool.icon} />
                <h3>{tool.title}</h3>
                <p>{tool.text}</p>
                <button
                  className="interactive-tool-action"
                  onClick={(event) => {
                    event.stopPropagation();
                    openTool(tool.path);
                  }}
                >
                  {tool.action}
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="faq-section" id="faq">
        <div className="container faq-layout">
          <div>
            <span className="section-kicker">Common questions</span>
            <h2>FAQs About Medical Coding Classes Online</h2>
          </div>

          <div className="faq-list">
            <details>
              <summary>
                <span>Can I learn medical billing and coding online?</span>
                <SafeIcon icon={FiHelpCircle} />
              </summary>
              <p>
                Yes. Our study guide provides 100% instant online access to core
                curriculum modules covering ICD-10-CM, CPT®, HCPCS Level II, and
                medical decision-making (MDM) without requiring paid course
                enrollment or mandatory fees.
              </p>
            </details>

            <details>
              <summary>
                <span>Do I need previous healthcare experience?</span>
                <SafeIcon icon={FiHelpCircle} />
              </summary>
              <p>
                No. The curriculum starts with terminology and industry
                foundations before introducing coding concepts.
              </p>
            </details>

            <details>
              <summary>
                <span>Is my progress saved?</span>
                <SafeIcon icon={FiHelpCircle} />
              </summary>
              <p>
                Yes. Completed lessons are stored locally in your current
                browser without requiring an account.
              </p>
            </details>

            <details>
              <summary>
                <span>
                  What is the difference between AAPC (CPC) and AHIMA
                  (CCA/CCS-P) certifications?
                </span>
                <SafeIcon icon={FiHelpCircle} />
              </summary>
              <p>
                AAPC offers the Certified Professional Coder (CPC®) credential,
                which focuses heavily on physician office and outpatient
                procedural coding. AHIMA offers the Certified Coding Associate
                (CCA®) and Certified Coding Specialist - Physician-based
                (CCS-P®) credentials, covering both inpatient and outpatient
                settings.
              </p>
            </details>

            <details>
              <summary>
                <span>
                  How do interactive tools like the E/M Calculator and Modifier
                  Selector help on coding exams?
                </span>
                <SafeIcon icon={FiHelpCircle} />
              </summary>
              <p>
                Interactive calculators break down complex guidelines—such as
                2023/2024 AMA Medical Decision Making scoring rules and CPT
                modifier selection (-25, -59, -50)—into clear decision steps.
                Testing yourself with live scenarios builds the clinical
                intuition needed to solve exam case studies quickly.
              </p>
            </details>

            <details>
              <summary>
                <span>
                  Are these practice modules aligned with current ICD-10-CM and
                  CPT guidelines?
                </span>
                <SafeIcon icon={FiHelpCircle} />
              </summary>
              <p>
                Yes, all lessons, quizzes, and decision trees are aligned with
                official CMS, AMA, and ICD-10-CM coding guidelines and
                conventions used across AAPC and AHIMA exam blueprints.
              </p>
            </details>

            <details>
              <summary>
                <span>Is this a certification program?</span>
                <SafeIcon icon={FiHelpCircle} />
              </summary>
              <p>
                No. This is an independent educational study resource and does
                not award credentials or certification.
              </p>
            </details>
          </div>
        </div>
      </section>

      <script type="application/ld+json">
        {JSON.stringify(faqStructuredData)}
      </script>

      <CareerOutlook />
    </>
  );
}

export default StudyTools;